import React, { useState, useEffect, useRef } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import Callout from '@/components/General/Callout';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveCustomField } from "@/data/customFields";
import InputSection from '@/components/General/inputs/InputSection';
import Toast from 'react-native-toast-message'
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import PlanRestrictedScreen from '@/components/General/PlanRestrictedScreen';

const EMPTY_DRAFT = {};

export default function AddCustomFieldModal() {
    const { entitlements } = useOrganizationEntitlements();
    if (isFreeOrg(entitlements)) return <PlanRestrictedScreen />;
    return <AddCustomFieldModalInner />;
}

function AddCustomFieldModalInner() {
    const { id, sectionID, module } = useLocalSearchParams()
    const storeKey = id || "new"

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const customFieldDetails = useEditScreenStore(s => s.AddCustomFieldModal?.[storeKey]) || EMPTY_DRAFT

    /*** State ***/
    const [ dropdownOptions, setDropdownOptions ] = useState([]);
    const [ hasRemovedOption, setHasRemovedOption ] = useState(false);
    const originalOptionsRef = useRef(null);

    /*** Theme & Dimensions ***/
    const {theme: {colors, fonts}} = useUnistyles();
    const router = useRouter()

    /*** Queries ***/
    const { data: customFieldToEdit } = Query.customFields.byId({id, options: {enabled: !!id, placeholderData: {}}})


    /*** Effects ***/
    useEffect(() => {
        if(customFieldToEdit){
            setCustomFieldDetails({
                ...customFieldToEdit,
                sectionID: customFieldToEdit.Section?.id,
                originalValues: customFieldToEdit,
            })
            const opts = customFieldToEdit.options || [];
            setDropdownOptions(opts);
            if(originalOptionsRef.current === null) originalOptionsRef.current = opts;
        }
    }, [customFieldToEdit]);


    useEffect(() => {
        if(sectionID){
            setCustomFieldDetails({
                ...customFieldDetails,
                sectionID: sectionID
            })
        }

        if(module){
            setCustomFieldDetails({
                ...customFieldDetails,
                module: module
            })
        }
    }, [sectionID, module]);

    const setCustomFieldDetails = (details) => {
        setEditScreenData("AddCustomFieldModal", storeKey, details)
    }


    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: loggedInUser } = Query.people.loggedInUser()

    const customFieldMutation = useMutation({
        mutationFn: saveCustomField,
        onSuccess: () => {
            const module = customFieldDetails?.module || "People";
            queryClient.invalidateQueries({queryKey: QueryKeys.customFields.all(module)})
            queryClient.invalidateQueries({queryKey: QueryKeys.customFieldSections.all(module)})
            queryClient.invalidateQueries({queryKey: QueryKeys.personCustomFields.all()})
            queryClient.invalidateQueries({queryKey: QueryKeys.personCustomFieldSections.all()})
            queryClient.invalidateQueries({queryKey: QueryKeys.eventCustomFields.all()})
            queryClient.invalidateQueries({queryKey: QueryKeys.eventCustomFieldSections.all()})
            queryClient.invalidateQueries({queryKey: QueryKeys.smallGroupTypes.all()})
            removeEditScreenData("AddCustomFieldModal", storeKey)
            router.back();
        }
    })


    const dropdown_option_inputs = dropdownOptions?.map((value, i) => {
        const isExistingOption = originalOptionsRef.current?.includes(value);
        return <View key={"dropdown" + i}><Input
                title={"Option " + (i + 1)}
                value={value}
                onUpdate={(new_value) => {
                    setDropdownOptions(prev => {
                        let options = [...prev];
                        options[i] = new_value;

                        return options
                    });
                }}
            />
            <TouchableOpacity style={{flexDirection:"row", alignItems:"center"}} onPress={() => {
                if(isExistingOption) setHasRemovedOption(true);
                setDropdownOptions(prev => {
                    let options = [...(prev || [])];
                    options.splice(i, 1);

                    return options
                });
            }}><Text style={{color:colors.error, fontSize:12, marginLeft:15, marginTop:-5, marginBottom:5}}>Remove</Text></TouchableOpacity>
        </View>
    });


    const onSubmit = () => {
        if(new Set(dropdownOptions).size !== dropdownOptions.length){
            Toast.show({
                type: 'error',
                text2: `You can't have duplicate dropdown options`
            });
            return true;
        }

        if(!customFieldDetails.name || !customFieldDetails.type || !customFieldDetails.sectionID){
            Toast.show({
                type: 'error',
                text2: `Please fill out all required fields`
            });
            return true;
        }

        const { originalValues, ...cleanDetails } = customFieldDetails;
        customFieldMutation.mutate({details: {...cleanDetails, options: dropdownOptions || null}, originalValues})
    }


    /*** Component Methods ***/
    const getInputDescriptionText = () => {
        switch(customFieldDetails.type){
            case "Text":
                return "A standard text input. This is for entering short words or phrases, and any other generic text."
            case "Long Text":
                return "A multiline input for entering long strings of text. This is good for notes, descriptions, or longer inputs that may require seeing multiple lines at a time when typing."
            case "Date":
                return "A date input for accepting only a specific date. This field will reveal a calendar picker for the user to select a date."
            case "Datetime":
                return "A combined date and time input. This will be a single field with two separate inputs. This will reveal a calendar picker for the date, and a time selectore for the time. The two will be combined into a single value. Ex: 01/01/2023 3:30pm"
            case "Number":
                return "A standard text input that will only accept numbers."
            case "Checkbox":
                return "A checkbox style input that will be 'On' or 'Off'. This will appear as a switch on most devices."
            case "Dropdown":
                return "A multiple choice dropdown selection that the user may choose a single item from."
            case "Multiselect":
                return "A multiple choice selection that the user may choose any number of items from."
            case "File Upload":
                return "A file upload input that will allow the user to upload a file from their device."
        }
    }

    const typeOptions = [];
    loggedInUser?.can?.("Create Custom Fields") && typeOptions.push("People", "Events", "SmallGroupType");

    return (
        <ModalWrapper
            loading={customFieldMutation.isPending} 
            onConfirm={onSubmit} 
            title={id ? "Edit Custom Field" : "New Custom Field"} 
        >
        
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
                <View style={{paddingBottom:20 }}>
                    <InputSection title="Custom Field Details">
                        {/* <SelectInput
                            disabled={!!id}
                            placeholder="Module"
                            options={typeOptions}
                            title="Module"
                            value={customFieldDetails?.module}
                            onUpdate={(new_value) => {
                                setCustomFieldDetails({
                                    module: new_value
                                })
                            }}
                        /> */}

                        {/* Section selector hidden — sections are managed on the main Custom Fields page via drag and drop. */}

                        <Input
                            title="Field Name"
                            value={customFieldDetails.name}
                            onUpdate={(new_value) => {
                                setCustomFieldDetails({
                                    name: new_value
                                })
                            }}
                        />

                    
                        <SelectInput
                            placeholder="Select an Input Type"
                            backgroundColor={colors.surface}
                            options={["Text", "Long Text", "Date", "Datetime", "Number", "Checkbox", "Dropdown", "Multiselect", "File Upload"]}
                            title="Input Type"
                            value={customFieldDetails.type}
                            onUpdate={(new_value) => {
                                setCustomFieldDetails({
                                    type: new_value
                                })
                            }}
                        />

                        <Text style={{fontStyle:"italic", fontSize:14, color:colors.textSecondary, marginHorizontal:10, marginBottom:10}}>{getInputDescriptionText()}</Text>

                        <ToggleRow
                            label="Required"
                            description="Make this field required when filling out the form."
                            value={customFieldDetails.required || false}
                            onValueChange={(new_value) => {
                                setCustomFieldDetails({
                                    required: new_value
                                })
                            }}
                        />
                    </InputSection>

                    { customFieldDetails.type && ["Dropdown", "Multiselect"].includes(customFieldDetails.type) 
                        ? <InputSection title="Selection Options" description="Add the options here that you want the users to be able to choose from.">
                                {hasRemovedOption && (
                                    <View style={{marginBottom: 10}}>
                                        <Callout
                                            type="warning"
                                            title="Option Removed"
                                            message="Removing an option will remove it from the list of choices, but will not change the value for any people who already have it selected."
                                        />
                                    </View>
                                )}
                                {dropdown_option_inputs}
                                
                                <TouchableOpacity style={{flexDirection:"row", alignItems:"center", marginTop:10, marginBottom:12}} onPress={() => {
                                    setDropdownOptions(prev => {
                                        let options = [...prev];
                                        options.push("");

                                        return options
                                    });
                                }}><Text style={{fontFamily:fonts.body, color:colors.primary, fontSize:16}}>{dropdownOptions.length == 0 ? "Add an option" : "Add another option"}</Text></TouchableOpacity>
                            </InputSection>
                        : <View></View>
                    }

                    { customFieldDetails.type && customFieldDetails.type == "File Upload"
                        ? <InputSection title="Allowed File Types" description="Add the file extentions here (.pdf, .jpg, .docx, etc.) that you want the users to be able to upload. Leave empty to allow any file type.">
                                {dropdown_option_inputs}
                                
                                <TouchableOpacity style={{flexDirection:"row", alignItems:"center", marginTop:10, marginBottom:12}} onPress={() => {
                                    setDropdownOptions(prev => {
                                        let options = [...prev];
                                        options.push("");

                                        return options
                                    });
                                }}><Text style={{fontFamily:fonts.body, color:colors.primary, fontSize:16}}>{dropdownOptions.length == 0 ? "Add a file type" : "Add another file type"}</Text></TouchableOpacity>
                            </InputSection>
                        : <View></View>
                    }
                
                </View>
            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}